// src/pages/UserPage/sections/OrganizationDeliveries/netvisor/InvoicePreviewModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Divider, Alert, CircularProgress, Button,
  Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, Tooltip, IconButton,
} from '@mui/material';
import { Edit, Check, RotateCcw } from 'lucide-react';
import { doc, writeBatch } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import {
  fmtEur,
  buildInvoiceLineText,
  byTargetTimeDesc,
  getEffectiveFreeText,
  getEffectivePrice,
  isOverridden,
  resolveBilling,
  BATCH_LIMIT,
  chunk,
} from './invoicePreviewHelpers';

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('fi-FI', {
    day: 'numeric', month: 'short', year: 'numeric',
    timeZone: 'Europe/Helsinki',
  });
}

// ─── Fix missing billing section ─────────────────────────────────────────────

function groupNeedsFixBySender(needsFix, members) {
  const map = new Map();
  for (const d of needsFix) {
    const key = d.senderId || `no-sender:${d.id}`;
    if (!map.has(key)) {
      const member = members.find((m) => m.userId === d.senderId);
      map.set(key, {
        key,
        senderId:         d.senderId || null,
        senderName:       d.senderName || member?.name || '—',
        suggestedCompany: d.billing?.companyName || member?.billing?.companyName || member?.name || '',
        deliveries:       [],
      });
    }
    map.get(key).deliveries.push(d);
  }
  return [...map.values()]
    .map((g) => ({ ...g, total: g.deliveries.reduce((s, d) => s + (Number(d.price) || 0), 0) }))
    .sort((a, b) => b.deliveries.length - a.deliveries.length);
}

function FixMissingBillingSection({ open, needsFix, members, organizationId, onFixed, disabled, onSavingChange }) {
  const [edits, setEdits]         = useState({});
  const [savingFix, setSavingFix] = useState(false);

  const fixGroups = useMemo(() => groupNeedsFixBySender(needsFix, members), [needsFix, members]);
  const fixKey    = useMemo(() => fixGroups.map((g) => g.key).join(','), [fixGroups]);

  useEffect(() => {
    if (!open) return;
    const seed = {};
    for (const g of fixGroups) seed[g.key] = { companyName: g.suggestedCompany, yNumber: '' };
    setEdits(seed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fixKey]);

  const setSaving = (v) => { setSavingFix(v); onSavingChange?.(v); };
  const setEdit   = (key, field) => (e) =>
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [field]: e.target.value } }));

  const readyGroups          = fixGroups.filter((g) => (edits[g.key]?.yNumber || '').trim());
  const fixableDeliveryCount = readyGroups.reduce((s, g) => s + g.deliveries.length, 0);
  const busy                 = disabled || savingFix;

  const handleSaveFixes = async () => {
    const targets = fixGroups.filter((g) => {
      const e = edits[g.key] || {};
      return (e.yNumber || '').trim() || (e.companyName || '').trim();
    });
    if (!targets.length) return;
    setSaving(true);
    try {
      const writes = [];
      for (const g of targets) {
        const e = edits[g.key];
        for (const d of g.deliveries) {
          writes.push([d.id, {
            billing: {
              ...(d.billing || {}),
              companyName: (e.companyName || '').trim() || null,
              yNumber:     (e.yNumber     || '').trim() || null,
            },
          }]);
        }
      }
      for (const part of chunk(writes, BATCH_LIMIT)) {
        const batch = writeBatch(db);
        for (const [id, patch] of part) {
          batch.update(doc(db, 'organizations', organizationId, 'deliveries', id), patch);
        }
        await batch.commit();
      }
      writes.forEach(([id, patch]) => onFixed?.(id, patch));
    } catch (err) {
      console.error('Fix billing failed:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!fixGroups.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        {needsFix.length} toimitukselta puuttuu Y-tunnus ({fixGroups.length} lähettäjä
        {fixGroups.length > 1 ? 'ä' : ''}). Täydennä tiedot ja tallenna.
      </Alert>

      {fixGroups.map((g) => {
        const e = edits[g.key] || { companyName: '', yNumber: '' };
        return (
          <Box key={g.key} sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>{g.senderName}</Typography>
                <Chip size="small" variant="outlined" label={`${g.deliveries.length} toimitusta`}
                  sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
              <Typography variant="caption" color="text.secondary">{fmtEur(g.total)}</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField label="Laskutuksen yritys" size="small" value={e.companyName}
                onChange={setEdit(g.key, 'companyName')} disabled={busy} fullWidth />
              <TextField label="Y-tunnus" size="small" value={e.yNumber}
                onChange={setEdit(g.key, 'yNumber')} placeholder="1234567-8"
                error={!(e.yNumber || '').trim()}
                helperText={!(e.yNumber || '').trim() ? 'Puuttuu' : `Asetetaan ${g.deliveries.length} toimitukselle`}
                disabled={busy} fullWidth />
            </Box>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
        <Button size="small" variant="outlined" color="warning"
          startIcon={savingFix ? <CircularProgress size={14} color="inherit" /> : <Edit size={14} />}
          onClick={handleSaveFixes}
          disabled={busy || fixableDeliveryCount === 0}>
          {savingFix ? 'Tallennetaan...' : `Tallenna korjaukset${fixableDeliveryCount ? ` (${fixableDeliveryCount})` : ''}`}
        </Button>
      </Box>
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

// ─── Ready-to-send / preview groups ──────────────────────────────────────────

function ReadyGroups({ groups, overrides, onOverride, onResetOverride, disabled, isNetvisor }) {
  const [editingId, setEditingId] = useState(null);

  const toggleEdit = (d, yNumber) => {
    if (editingId === d.id) { setEditingId(null); return; }
    if (overrides[d.id]?.freeText === undefined) {
      onOverride?.(d.id, 'freeText', buildInvoiceLineText(d, yNumber));
    }
    setEditingId(d.id);
  };

  return (
    <>
      {groups.map((g, gi) => {
        const rows     = [...g.deliveries].sort(byTargetTimeDesc);
        const subtotal = rows.reduce((s, d) => s + getEffectivePrice(d, overrides), 0);

        return (
          <Box key={g.yNumber || gi} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>{g.companyName || '—'}</Typography>
              {g.yNumber && <Chip size="small" variant="outlined" label={`Y: ${g.yNumber}`} />}
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {rows.length} riviä
              </Typography>
            </Box>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 90 }}>Tuote</TableCell>
                  {isNetvisor
                    ? <TableCell>Selite</TableCell>
                    : <>
                        <TableCell>Pvm</TableCell>
                        <TableCell>Lähtö</TableCell>
                        <TableCell>Määränpää</TableCell>
                      </>
                  }
                  <TableCell align="right" sx={{ width: 110 }}>Veroton €</TableCell>
                  {isNetvisor && <TableCell sx={{ width: 56 }} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((d) => {
                  const editing = isNetvisor && editingId === d.id;
                  const edited  = isNetvisor && isOverridden(d, overrides);
                  const price   = overrides[d.id]?.price;

                  return (
                    <TableRow key={d.id} sx={editing ? { bgcolor: 'action.hover' } : undefined}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography variant="caption" fontWeight={600}>Lähetys</Typography>
                        {edited && (
                          <Chip label="muokattu" size="small" color="info" variant="outlined"
                            sx={{ height: 16, fontSize: '0.6rem', display: 'flex', width: 'fit-content', mt: 0.5 }} />
                        )}
                      </TableCell>

                      {isNetvisor ? (
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          {editing ? (
                            <TextField
                              value={getEffectiveFreeText(d, g.yNumber, overrides)}
                              onChange={(e) => onOverride?.(d.id, 'freeText', e.target.value)}
                              size="small" fullWidth multiline minRows={2} disabled={disabled}
                              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.4 } }}
                            />
                          ) : (
                            <Typography variant="caption" fontFamily="monospace"
                              sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>
                              {getEffectiveFreeText(d, g.yNumber, overrides)}
                            </Typography>
                          )}
                        </TableCell>
                      ) : (
                        <>
                          <TableCell><Typography variant="caption">{fmtDate(d.completedAtMs)}</Typography></TableCell>
                          <TableCell><Typography variant="caption" noWrap sx={{ maxWidth: 120, display: 'block' }}>{d.origin || '—'}</Typography></TableCell>
                          <TableCell><Typography variant="caption" noWrap sx={{ maxWidth: 120, display: 'block' }}>{d.destination || '—'}</Typography></TableCell>
                        </>
                      )}

                      <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                        {isNetvisor && editing ? (
                          <TextField
                            value={price !== undefined ? price : String(d.price ?? '')}
                            onChange={(e) => onOverride?.(d.id, 'price', e.target.value)}
                            size="small" disabled={disabled}
                            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', fontSize: '0.75rem' } }}
                            sx={{ width: 90 }}
                          />
                        ) : (
                          <Typography variant="caption" fontWeight={600}>
                            {fmtEur(getEffectivePrice(d, overrides))}
                          </Typography>
                        )}
                      </TableCell>

                      {isNetvisor && (
                        <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {edited && !editing && (
                              <Tooltip title="Palauta alkuperäinen">
                                <IconButton size="small" onClick={() => onResetOverride?.(d.id)} disabled={disabled}>
                                  <RotateCcw size={14} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title={editing ? 'Valmis' : 'Muokkaa riviä'}>
                              <IconButton size="small" onClick={() => toggleEdit(d, g.yNumber)} disabled={disabled}>
                                {editing ? <Check size={14} /> : <Edit size={14} />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}

                <TableRow>
                  <TableCell colSpan={isNetvisor ? 2 : 4} align="right" sx={{ border: 0 }}>
                    <Typography variant="caption" color="text.secondary">Veroton yhteensä</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ border: 0 }}>
                    <Typography variant="body2" fontWeight={700}>{fmtEur(subtotal)}</Typography>
                  </TableCell>
                  {isNetvisor && <TableCell sx={{ border: 0 }} />}
                </TableRow>
              </TableBody>
            </Table>

            {gi < groups.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        );
      })}
    </>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

/**
 * Props:
 *   open, onClose, onConfirm(overrides)
 *   groups       — ready-to-send groups from buildNetvisorSelection
 *   needsFix     — deliveries missing Y-number (pass [] for PDF/XML)
 *   members, organizationId, onFixed
 *   skippedCount — intentional noBilling rows
 *   sending      — loading state
 *   vatRate
 *   confirmLabel — button label, e.g. "Luo PDF", "Luo XML", "Lähetä Netvisoriin"
 *   isNetvisor   — whether to show editable Selite/price columns
 */
export const InvoicePreviewModal = ({
  open,
  onClose,
  onConfirm,
  groups = [],
  needsFix = [],
  members = [],
  organizationId,
  onFixed,
  skippedCount = 0,
  sending = false,
  vatRate = 25.5,
  confirmLabel = 'Lähetä Netvisoriin',
  isNetvisor = false,
}) => {
  const [savingFix, setSavingFix] = useState(false);
  const [overrides, setOverrides] = useState({});

  useEffect(() => { if (open) setOverrides({}); }, [open]);

  const handleOverride      = (id, field, value) =>
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  const handleResetOverride = (id) =>
    setOverrides((prev) => { const next = { ...prev }; delete next[id]; return next; });

  const resolvedOverrides = useMemo(() => {
    if (!isNetvisor) return {};
    const out = {};
    for (const g of groups) {
      for (const d of g.deliveries) {
        if (!isOverridden(d, overrides)) continue;
        out[d.id] = {
          freeText: getEffectiveFreeText(d, g.yNumber, overrides),
          price:    getEffectivePrice(d, overrides),
        };
      }
    }
    return out;
  }, [groups, overrides, isNetvisor]);

  const editedCount     = Object.keys(resolvedOverrides).length;
  const totalDeliveries = groups.reduce((s, g) => s + g.deliveries.length, 0);
  const grandTotal      = groups.reduce(
    (s, g) => s + g.deliveries.reduce((gs, d) => gs + getEffectivePrice(d, overrides), 0),
    0,
  );

  const busy    = sending || savingFix;
  const isEmpty = groups.length === 0 && needsFix.length === 0;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Esikatselu — {confirmLabel}
        {groups.length > 1 ? ` (${groups.length} laskua)` : ''}
      </DialogTitle>

      <DialogContent dividers>
        {isEmpty ? (
          <Typography color="text.secondary" textAlign="center" py={3}>
            Ei laskutettavia toimituksia
          </Typography>
        ) : (
          <>
            {skippedCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {skippedCount} toimitusta ohitetaan (ei laskutusta).
              </Alert>
            )}

            {/* Fix missing Y-numbers — only relevant for Netvisor */}
            {isNetvisor && (
              <FixMissingBillingSection
                open={open}
                needsFix={needsFix}
                members={members}
                organizationId={organizationId}
                onFixed={onFixed}
                disabled={sending}
                onSavingChange={setSavingFix}
              />
            )}

            <ReadyGroups
              groups={groups}
              overrides={overrides}
              onOverride={handleOverride}
              onResetOverride={handleResetOverride}
              disabled={busy}
              isNetvisor={isNetvisor}
            />

            {groups.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, alignItems: 'baseline' }}>
                  <Typography variant="caption" color="text.secondary">
                    {groups.length} lasku{groups.length > 1 ? 'a' : ''} · {totalDeliveries} riviä
                    {editedCount > 0 ? ` · ${editedCount} muokattu` : ''}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Veroton yhteensä
                    </Typography>
                    <Typography variant="h6" fontWeight={800}>{fmtEur(grandTotal)}</Typography>
                  </Box>
                </Box>

                {isNetvisor && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    ALV {vatRate} % ja mahdollinen palvelulisä lisätään Netvisorissa laskutusasetusten mukaan.
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Peruuta</Button>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
          onClick={() => onConfirm?.(resolvedOverrides)}
          disabled={busy || groups.length === 0}
        >
          {busy ? 'Käsitellään...' : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoicePreviewModal;